import React, { useCallback, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { connect, ResolveThunks } from 'react-redux';
import { fetchAlerts as fetchAlertsAction, fetchUpdatedAlert as fetchUpdatedAlertAction } from './alertActions';
import { fetchReasons as fetchReasonsAction } from '../scenario/scenariosActions';
import { AppState } from '../store/store';
import AlertRowAssignment from './AlertRowAssignment';
import './Alerts.css';
import { useQueryUsersBasic } from '../users/userActions';
import Table from '../table/Table';
import { ApiScenarioEntityType } from '../api';
import { getUserId, getUserName } from '../auth/authReducer';
import { setPage } from './alertReducer';
import { MonitoringAlertHistory } from './AlertHistory';
import { useAlertsLiveUpdates } from './useAlertsLiveUpdates';
import { isNavigableCellClicked } from '../common/helper';
import FormattedDateTime from '../common/datetime/FormattedDateTime';
import {
  updateOrderByAction,
  updateSelectedAssigneeAction,
  updateSelectedReasonAction,
  updateSelectedScenarioTypeAction,
  updateSelectedStatusAction,
} from './filters/filtersActions';
import { OrderBy, SelectOption } from './filters/filtersReducer';
import AlertDetailsPopover from './AlertDetailsPopover';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { PersonLink } from '../common/PersonLink';
import { ALL_VALUE } from './helper';
import { apiStatusesToFetch, getFilterAlertStatuses, statusDefault } from './alertStatusHelper';
import { apiScenarioTypeToFetch, getFilterScenarioTypes, scenarioTypeDefault } from './scenarioTypeHelper';
import { reasonDefault } from './reasonsHelper';
import { assigneeDefault } from './assigneeHelper';
import { fetchAlertStatuses } from '../screening/alert-status/alertStatusesActions';
import { useNavigate } from 'react-router';
import {
  InternalApiEntityType,
  InternalApiMonitoringAlert,
  InternalApiScenarioType,
  InternalApiSortDirection,
  InternalApiUserBasicDetails,
} from '../api-internal';
import { SalvDropdown, SalvProgressCircle } from '@salvapp/ui-components';
import { useMe } from '../organisation/useMe';
import SalvPage from '../common/SalvPage';
import { useQueryAttributeMappings } from '../schema/mappings/mappingsActions';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';

export const PersonAlerts = ({
  page,
  size,
  total,
  alerts,
  statusOptions,
  allStatuses,
  activeStatuses,
  fetchAlerts,
  fetchUpdatedAlert,
  setPage,
  fetchReasons,
  reasons,
  scenarioTypes,
  currentUser,
  filters,
  updateSelectedAssignee,
  updateSelectedStatus,
  updateSelectedReason,
  updateSelectedScenarioType,
  updateOrderBy,
  fetchAlertStatuses,
}: ComponentProps) => {
  const navigate = useNavigate();
  const navigateToCustomer = (id: string) => navigate(`/customer/${id}`);
  const navigateToMonitoringAlert = (id: number) => navigate(`/monitoring/alerts/${id}`);
  const { data: users } = useQueryUsersBasic();
  const { features } = useMe();

  useEffect(() => {
    fetchReasons(ApiScenarioEntityType.PERSON);
  }, [fetchReasons]);

  useEffect(() => {
    if (allStatuses.length == 0) {
      fetchAlertStatuses();
    }
  }, [allStatuses, fetchAlertStatuses]);

  const userValues = [
    {
      key: ALL_VALUE,
      value: ALL_VALUE,
      label: 'All users',
    },
    {
      key: currentUser.key,
      value: String(currentUser.value),
      label: 'Assigned to me',
    },
  ].concat(
    users?.map((u: InternalApiUserBasicDetails) => ({
      key: String(u.id),
      value: String(u.id),
      label: u.email || '',
    })) || []
  );

  const [fetching, setFetching] = useState(false);
  const [failedToLoadAlerts, setFailedToLoadAlerts] = useState(false);

  const fetchNewAlerts = useCallback(
    async ({ background = false }: { background?: boolean } = {}) => {
      if (!background) {
        setFetching(true);
        setFailedToLoadAlerts(false);
      }
      const assigneeId = filters.selectedAssignee?.value !== ALL_VALUE ? filters.selectedAssignee?.value : undefined;

      const getStatusesToFetch = apiStatusesToFetch(filters.selectedStatus || statusDefault, activeStatuses);

      const reason = filters.selectedReason?.value !== ALL_VALUE ? filters.selectedReason?.value : undefined;

      const scenarioIds = undefined;

      const scenarioTypes = (): InternalApiScenarioType[] | undefined => {
        return apiScenarioTypeToFetch(filters.selectedScenarioType || scenarioTypeDefault);
      };

      try {
        await fetchAlerts(
          page,
          size,
          undefined,
          getStatusesToFetch,
          scenarioTypes(),
          assigneeId,
          reason,
          scenarioIds,
          undefined,
          filters.orderBy.attribute,
          filters.orderBy.direction && InternalApiSortDirection[filters.orderBy.direction],
          InternalApiEntityType.PERSON
        );
        setFailedToLoadAlerts(false);
      } catch (_) {
        setFailedToLoadAlerts(true);
      }
      if (!background) {
        setFetching(false);
      }
    },
    [
      page,
      size,
      fetchAlerts,
      filters.selectedStatus,
      filters.selectedAssignee,
      filters.selectedReason,
      filters.selectedScenarioType,
      filters.orderBy,
    ]
  );

  const handleSelectChange = (value: any, assignerMethod: (value: any) => void) => {
    setPage(0);
    assignerMethod(value);
  };

  useEffect(() => {
    fetchNewAlerts();
  }, [fetchNewAlerts]);

  useAlertsLiveUpdates({
    fetchUpdatedAlert,
    refreshAlerts: () => fetchNewAlerts({ background: true }),
  });

  const navigateTo = (alert: InternalApiMonitoringAlert) => {
    if (isFeatureEnabled(ORGANISATION_FEATURE.MONITORING_ALERT_PAGE, features)) {
      return navigateToMonitoringAlert(alert.id);
    }
    return navigateToCustomer(alert.person.id);
  };

  const {
    data: attributeMappings,
    isPending: isAttributeMappingsPending,
    isError: isAttributeMappingsError,
  } = useQueryAttributeMappings();

  if (isAttributeMappingsError) {
    return <DataLoadingErrorFallback message={'Could not load Attribute Mappings'} />;
  } else if (isAttributeMappingsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Attribute Mappings'} />;
  }

  const renderAlerts = () => {
    return alerts.map((alert: InternalApiMonitoringAlert) => (
      <tr
        key={alert.id}
        onClick={(event: any) => {
          isNavigableCellClicked(event) && navigateTo(alert);
        }}
        className='clickable-row'
      >
        <td>
          <PersonLink person={alert.person} mappings={attributeMappings.map} />
        </td>
        <td>
          <FormattedDateTime date={alert.createdTime} showTime />
        </td>
        <td className='alert-details'>
          <AlertDetailsPopover alert={alert} />
        </td>
        <td>
          <AlertRowAssignment alert={alert} />
        </td>
        <td>{allStatuses.find((status) => status.id === alert.statusId)?.textName}</td>
        <td>
          <MonitoringAlertHistory alert={alert} />
        </td>
      </tr>
    ));
  };

  return (
    <SalvPage title={'Person monitoring alerts'}>
      <div className='header'>
        <Row>
          <Col md={3}>
            <SalvDropdown
              label={'Assigned to'}
              options={userValues}
              value={filters.selectedAssignee || assigneeDefault}
              clearable={false}
              onChange={([value]) => {
                handleSelectChange(value, updateSelectedAssignee);
              }}
            />
          </Col>
          <Col md={3}>
            <SalvDropdown
              label={'Alert status'}
              options={statusOptions}
              value={filters.selectedStatus || statusDefault}
              clearable={false}
              onChange={([value]) => {
                handleSelectChange(value, updateSelectedStatus);
              }}
            />
          </Col>
          <Col md={3}>
            <SalvDropdown
              label={'Reason'}
              options={reasons}
              value={filters.selectedReason || reasonDefault}
              clearable={false}
              onChange={([value]) => {
                handleSelectChange(value, updateSelectedReason);
              }}
            />
          </Col>
          <Col md={3}>
            <SalvDropdown
              label={'Scenario type'}
              options={scenarioTypes}
              value={filters.selectedScenarioType || scenarioTypeDefault}
              clearable={false}
              onChange={([value]) => {
                handleSelectChange(value, updateSelectedScenarioType);
              }}
            />
          </Col>
        </Row>
      </div>
      <Table
        responsive={false}
        total={total}
        page={page}
        size={size}
        items={alerts}
        fetchPage={setPage}
        attributes={[
          { key: 'Person', sortable: false },
          { key: 'Time', sortable: true },
          { key: 'Details', sortable: false },
          { key: 'Assignee', sortable: false },
          { key: 'Status', sortable: false },
          { key: 'Actions', sortable: false },
        ]}
        body={renderAlerts}
        onSort={(attribute, direction) => {
          updateOrderBy({ attribute, direction });
        }}
        fetching={fetching}
        emptyMessage={failedToLoadAlerts ? 'Failed to load alerts. Please try again.' : ''}
      />
    </SalvPage>
  );
};

const mapStateToProps = (state: AppState) => {
  const { alerts, scenarios, filters, schema, screeningAlertStatuses } = state;

  return {
    ...alerts,
    currentUser: {
      key: getUserName(state) || ALL_VALUE,
      value: getUserId(state) || ALL_VALUE,
      label: getUserName(state) || 'All',
    },
    statusOptions: getFilterAlertStatuses(screeningAlertStatuses.statuses),
    activeStatuses: screeningAlertStatuses.activeStatuses,
    allStatuses: screeningAlertStatuses.statuses,
    reasons: [
      {
        key: ALL_VALUE,
        value: ALL_VALUE,
        label: 'All reasons',
      },
    ].concat(
      scenarios.reasons.map((reason) => ({
        key: reason,
        value: reason,
        label: reason,
      }))
    ),
    scenarioTypes: getFilterScenarioTypes(),
    filters: filters,

    ...schema,
  };
};

const mapDispatchToProps = {
  fetchAlerts: fetchAlertsAction,
  fetchUpdatedAlert: fetchUpdatedAlertAction,
  fetchReasons: fetchReasonsAction,
  fetchAlertStatuses,
  setPage,
  updateSelectedReason: (selected: SelectOption) => updateSelectedReasonAction(selected),
  updateSelectedStatus: (selected: SelectOption) => updateSelectedStatusAction(selected),
  updateSelectedAssignee: (selected: SelectOption) => updateSelectedAssigneeAction(selected),
  updateSelectedScenarioType: (selected: SelectOption) => updateSelectedScenarioTypeAction(selected),
  updateOrderBy: (orderBy: OrderBy) => updateOrderByAction(orderBy),
};

type ComponentProps = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(PersonAlerts);
